
import React from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Tag } from "@/hooks/useTags";
import { MoreHorizontal, Edit, Trash2, Eye, EyeOff } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

interface TagItemProps {
  tag: Tag & { isSecondarySelected: boolean };
  isActive: boolean;
  isHidden: boolean;
  dropTargetId: string | null;
  onToggleSelection: (tagId: string, e: React.MouseEvent) => void;
  onEdit: (tag: Tag, e: React.MouseEvent) => void;
  onDelete: (tag: Tag, e: React.MouseEvent) => void;
  onToggleVisibility: (tag: Tag, e: React.MouseEvent) => void;
  onDragOver: (e: React.DragEvent, tagId: string) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent, tagId: string, tagName: string) => void;
}

const TagItem: React.FC<TagItemProps> = ({
  tag,
  isActive,
  isHidden,
  dropTargetId,
  onToggleSelection,
  onEdit,
  onDelete,
  onToggleVisibility,
  onDragOver,
  onDragLeave,
  onDrop
}) => {
  const getDroppableTagClass = () => 
    cn("flex items-center px-3 py-1.5 w-full h-full text-sm transition-colors rounded", {
      "bg-accent": isActive || dropTargetId === tag.id,
      "bg-accent/70": tag.isSecondarySelected,
      "opacity-50": isHidden,
      "hover:bg-accent/50": !dropTargetId || dropTargetId !== tag.id
    });

  return (
    <li 
      className="h-8 group relative"
      onDragOver={(e) => onDragOver(e, tag.id)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, tag.id, tag.name)}
    >
      <div className={getDroppableTagClass()}>
        <Link 
          to={`/tag/${tag.id}`}
          className="flex items-center w-full h-full"
        >
          <div 
            className="relative cursor-pointer h-5 w-5 mr-3"
            onClick={(e) => onToggleSelection(tag.id, e)}
            title={tag.isSecondarySelected ? "Remove from filter" : "Add to filter"}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill={tag.isSecondarySelected ? tag.color : "none"}
              stroke={tag.color} 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
              <circle 
                cx="7" 
                cy="7" 
                r="2" 
                fill={tag.isSecondarySelected ? "white" : "transparent"} 
                stroke={tag.isSecondarySelected ? "white" : tag.color}
              ></circle>
            </svg>
          </div>
          
          <span className={cn("truncate", {
            "line-through": isHidden
          })}>
            {tag.name}
          </span>
        </Link>
      </div>
      
      <div className="absolute right-2 top-1 flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="h-6 w-6 p-0"
              onClick={(e) => e.preventDefault()}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[220px]">
            <DropdownMenuItem onClick={(e) => onEdit(tag, e)}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => onToggleVisibility(tag, e)}>
              {isHidden ? (
                <>
                  <Eye className="mr-2 h-4 w-4" />
                  Show Tagged Content
                </>
              ) : (
                <>
                  <EyeOff className="mr-2 h-4 w-4" />
                  Hide Tagged Content
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={(e) => onDelete(tag, e)}
              className="text-red-500 focus:text-red-500"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </li>
  );
};

export default TagItem;
